import { useSharedValue } from '@epic-core/hooks';

import { RouterSharedKeys } from './router.keys';

/**
 * Represents a Route entry in the routing table
 */
export type Route<Origin extends string> = {
  /**
   * A pattern to match the pathname against. If set to `"*"` this route
   * is considered the default.
   *
   * If the pattern should match if it is a prefix of the pathname, add an
   * asteriks at the end, e.g. `/creative*`
   */
  pattern: string;
  /**
   * The origin it should route to.
   */
  origin: Origin;
  /**
   * The name of the launchdarkly flag that it should check to see if the route
   * is active. Always assumes it is diabled, unless LD responds with true.
   *
   * Flag should always be a boolean value and indicate if the route
   * should be enabled
   */
  flag?: string;

  /**
   * sensitive indicates if a route should be considered sensitive,
   * in which case it'll not be exposed on the public endpoint
   */
  sensitive?: boolean;
};

export type Routes<Origin extends string> = Array<Route<Origin>>;

export type RouterStoreProps = {
  routes: Routes<string>;
  isUnifiedHostname: boolean;
};

export const useOriginRoutes = (): RouterStoreProps['routes'] => {
  const [routerStore] = useSharedValue<RouterStoreProps>(RouterSharedKeys.RouterStore);
  return routerStore.routes || [];
};

export const useIsUnifiedHostname = (): RouterStoreProps['isUnifiedHostname'] => {
  const [routerStore] = useSharedValue<RouterStoreProps>(RouterSharedKeys.RouterStore);
  return routerStore.isUnifiedHostname;
};
